import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useAuth } from '../store';
import { getPublicEvents } from '../services/firestore';
import type { Event } from '../types';

/**
 * Landing Page
 * Entry point for the application
 * Shows join form and top events (when authenticated)
 */
export function LandingPage() {
  const [eventId, setEventId] = useState('');
  const [error, setError] = useState('');
  const [topEvents, setTopEvents] = useState<Event[]>([]);
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  // Load top 3 public events
  useEffect(() => {
    if (isAuthenticated) {
      const loadTopEvents = async () => {
        try {
          const publicEvents = await getPublicEvents();
          setTopEvents(publicEvents.slice(0, 3));
        } catch (err) {
          console.error('Failed to load top events:', err);
        }
      };
      loadTopEvents();
    }
  }, [isAuthenticated]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Require authentication before joining
    if (!isAuthenticated) {
      // Save the event ID to session storage to join after login
      sessionStorage.setItem('pendingEventJoin', eventId.trim());
      navigate('/login');
      return;
    }

    // Extract event ID from URL or direct input
    let extractedEventId = eventId.trim();

    // If user pasted full URL, extract event ID
    if (extractedEventId.includes('/join/')) {
      const match = extractedEventId.match(/\/join\/([^\/\s]+)/);
      if (match) {
        extractedEventId = match[1];
      }
    }

    // Validate event ID
    if (!extractedEventId) {
      setError('Please enter an event ID or shareable link');
      return;
    }

    // Navigate to join page
    navigate(`/join/${extractedEventId}`);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEventId(e.target.value);
    setError(''); // Clear error on input change
  };

  // Don't show loading state - just render immediately
  // Auth will update in background if needed
  if (isLoading) {
    console.log('⏳ LandingPage: Auth still loading, but showing UI anyway...');
    // Fall through to render the page
  }
  
  console.log('✅ LandingPage: Rendering (authenticated:', isAuthenticated, ')');

  // If not authenticated, show landing page with limited options
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-primary-bg relative overflow-hidden">
        {/* Vintage Betting Board Background - Video or Image */}
        {/* Video Version (uncomment to use) */}
        {/* <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        >
          <source src="/landing-bg.mp4" type="video/mp4" />
          <source src="/landing-bg.webm" type="video/webm" />
        </video> */}
        
        {/* Image Version - ACTIVE */}
        <div 
          className="absolute inset-0 w-full h-full bg-cover bg-center opacity-30"
          style={{ backgroundImage: 'url(/landing-bg.png)' }}
        />
        
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/60" />
        
        {/* Content */}
        <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              {/* Logo */}
              <div className="flex justify-center mb-6">
                <img 
                  src="/logo.png" 
                  alt="Fight Night Bookie" 
                  className="h-24 w-auto drop-shadow-2xl"
                />
              </div>
              <p className="text-secondary-text text-lg drop-shadow-lg">
                Join your fight night betting pool
              </p>
            </div>

            <Card padding="lg" className="backdrop-blur-sm bg-secondary-bg/90">
              <div className="space-y-4">
                <p className="text-center text-secondary-text">
                  Sign in to join events and start betting
                </p>

                <Button
                  type="button"
                  variant="primary"
                  fullWidth
                  onClick={() => navigate('/signup')}
                >
                  Get Started
                </Button>
                
                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
              </div>
            </Card>

            <div className="mt-6 text-center">
              <p className="text-muted-text text-sm drop-shadow-lg">
                Start with $1000 • Only winnings count • Bet it all to compete
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated view with join form and top events
  return (
    <div className="min-h-screen bg-primary-bg relative overflow-hidden">
      {/* Vintage Betting Board Background - Video or Image */}
      {/* Video Version (uncomment to use) */}
      {/* <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover opacity-20"
      >
        <source src="/landing-bg.mp4" type="video/mp4" />
        <source src="/landing-bg.webm" type="video/webm" />
      </video> */}
      
      {/* Image Version - ACTIVE */}
      <div 
        className="absolute inset-0 w-full h-full bg-cover bg-center opacity-20"
        style={{ backgroundImage: 'url(/landing-bg.png)' }}
      />
      
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/50" />
      
      {/* Content */}
      <div className="relative z-10 p-4">
        <div className="max-w-6xl mx-auto py-8">
          <div className="text-center mb-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src="/logo.png" 
                alt="Fight Night Bookie" 
                className="h-24 w-auto drop-shadow-2xl"
              />
            </div>
            <p className="text-secondary-text text-lg drop-shadow-lg">
              Join your fight night betting pool
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Join Event Form */}
            <Card padding="lg" className="backdrop-blur-sm bg-secondary-bg/90">
              <h2 className="text-xl font-bold text-primary-text mb-4">Join Event</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Event Link or ID"
                  placeholder="Enter shareable link or event ID"
                  value={eventId}
                  onChange={handleInputChange}
                  error={error}
                  helperText="Paste the shareable link or enter the event ID"
                  autoFocus
                />

                <Button type="submit" variant="primary" fullWidth>
                  Join Event
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-border space-y-3">
                <Button
                  type="button"
                  variant="primary"
                  fullWidth
                  onClick={() => navigate('/games')}
                >
                  My Events
                </Button>
                
                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  onClick={() => navigate('/create')}
                >
                  Create New Event
                </Button>
              </div>
            </Card>

            {/* Right: Top Events */}
            <Card padding="lg" className="backdrop-blur-sm bg-secondary-bg/90">
              <h2 className="text-xl font-bold text-primary-text mb-4">Top Events</h2>
              {topEvents.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-secondary-text">No public events available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {topEvents.map((event) => {
                    const totalBets = event.participants.reduce((sum, p) => sum + p.bets.length, 0);
                    
                    return (
                      <div key={event.id} className="p-4 bg-tertiary-bg/80 backdrop-blur-sm rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-semibold text-primary-text">{event.name}</h3>
                          {event.status === 'live' ? (
                            <Badge variant="error" size="sm">Live</Badge>
                          ) : (
                            <Badge variant="info" size="sm">Upcoming</Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                          <div>
                            <div className="text-xs text-secondary-text">Participants</div>
                            <div className="font-semibold text-primary-text">
                              {event.participants.length}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-secondary-text">Fights</div>
                            <div className="font-semibold text-primary-text">
                              {event.fights.length}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-secondary-text">Bets</div>
                            <div className="font-semibold text-primary-text">
                              {totalBets}
                            </div>
                          </div>
                        </div>

                        <Button
                          variant="primary"
                          size="sm"
                          fullWidth
                          onClick={() => navigate(`/join/${event.id}`)}
                        >
                          Join Event
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>

          <div className="mt-6 text-center">
            <p className="text-muted-text text-sm drop-shadow-lg">
              Start with $1000 • Only winnings count • Bet it all to compete
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
